(() => {
  'use strict';

  const GLOBAL_KEY = '__deebCigarProductRuntime';
  const RUNTIME_VERSION = 4;
  const STICKY_SHOW_THRESHOLD = -8;
  const STICKY_HIDE_THRESHOLD = 32;
  const ROOT_SELECTOR = '.deeb-cigar-product';
  const CONTRACT_SELECTOR =
    '[data-deeb-variant-contracts]';
  const VARIANT_PICKER_SELECTOR =
    'variant-selects, variant-radios';
  const MOBILE_MEDIA_QUERY =
    '(max-width: 749px)';
  const TRANSITION_TIMEOUT = 1400;
  const PRESERVED_QUERY_KEYS = [
    'view',
    'preview_theme_id'
  ];

  /*
   * Shopify Theme Editor and theme dev can evaluate this
   * asset more than once. Reuse only the matching runtime.
   * Older runtimes must be disposed so updated controller
   * logic actually becomes active after a hot reload.
   */
  const existingRuntime =
    window[GLOBAL_KEY];

  if (existingRuntime) {
    if (
      existingRuntime.version ===
      RUNTIME_VERSION
    ) {
      existingRuntime.initAll(document);
      return;
    }

    if (
      typeof existingRuntime
        .destroyRuntime === 'function'
    ) {
      existingRuntime.destroyRuntime();
    } else {
      existingRuntime.destroyAll?.(
        document
      );

      if (
        typeof existingRuntime
          .unsubscribeVariantChange ===
          'function'
      ) {
        existingRuntime
          .unsubscribeVariantChange();
      }

      if (
        window[GLOBAL_KEY] ===
        existingRuntime
      ) {
        delete window[GLOBAL_KEY];
      }
    }
  }

  const controllers = new WeakMap();
  const activeControllers = new Set();

  const normalizeId = (value) =>
    String(value ?? '').trim();

  const normalizeState = (value) => {
    const state = String(value || '').trim();

    /*
     * Preserve compatibility with markup from the
     * previous release while normalizing new states.
     */
    if (state === 'limit-reached') {
      return 'maximum-reached';
    }

    return [
      'available',
      'sold-out',
      'unavailable',
      'invalid-quantity',
      'maximum-reached',
      'insufficient-stock'
    ].includes(state)
      ? state
      : 'unavailable';
  };

  const toFiniteNumber = (
    value,
    fallback = 0
  ) => {
    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  };

  const toNullableNumber = (
    value,
    fallback = null
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ''
    ) {
      return fallback;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : fallback;
  };

  const afterRender = (callback) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(callback);
    });
  };

  class DeebCigarProductController {
    constructor(root) {
      this.root = root;

      this.sectionId =
        root.dataset.section ||
        root.dataset.sectionId ||
        '';

      this.stickyOwner =
        this.sectionId ||
        'deeb-cigar-product';

      this.mobileMediaQuery =
        window.matchMedia(
          MOBILE_MEDIA_QUERY
        );

      this.preservedQueryParameters =
        new Map();

      this.variantContracts = new Map();
      this.contractScriptElement = null;
      this.contractScriptContent = '';

      this.syncScheduled = false;
      this.pendingVariantId = undefined;
      this.pendingSettleTransition = false;

      this.variantTransitionActive = false;
      this.transitionLabel = '';
      this.transitionTimer = null;

      this.viewportFrameRequested = false;
      this.destroyed = false;

      this.stickyOriginalParent = null;
      this.stickyOriginalNextSibling = null;

      this.handleRootChange =
        this.handleRootChange.bind(this);

      this.handleQuantityInput =
        this.handleQuantityInput.bind(this);

      this.handleViewportChange =
        this.handleViewportChange.bind(this);

      this.handleMediaQueryChange =
        this.handleMediaQueryChange.bind(this);

      this.handleStructureMutation =
        this.handleStructureMutation.bind(this);

      this.handlePurchaseMutation =
        this.handlePurchaseMutation.bind(this);

      this.handleQuantityMutation =
        this.handleQuantityMutation.bind(this);

      this.captureQueryParameters();
      this.refreshElements();
      this.refreshVariantContracts();
      this.initialize();
    }

    captureQueryParameters() {
      const currentUrl = new URL(
        window.location.href
      );

      PRESERVED_QUERY_KEYS.forEach((key) => {
        const value =
          currentUrl.searchParams.get(key);

        if (
          value !== null &&
          value !== ''
        ) {
          this.preservedQueryParameters.set(
            key,
            value
          );
        }
      });
    }

    refreshElements() {
      this.infoContainer =
        this.root.querySelector(
          `[id="ProductInfo-${this.sectionId}"]`
        ) ||
        this.root.querySelector(
          '[id^="ProductInfo-"]'
        );

      this.purchaseBlock =
        this.root.querySelector(
          '[data-deeb-purchase-block]'
        );

      this.primaryButton =
        this.root.querySelector(
          '[data-deeb-primary-submit]'
        );

      this.variantInput =
        this.root.querySelector(
          '[data-deeb-variant-id]'
        );

      this.quantityInput =
        this.root.querySelector(
          '.quantity__input[name="quantity"]'
        );

      const mountedStickyPurchase =
        Array.from(
          document.querySelectorAll(
            '[data-deeb-sticky-purchase]'
          )
        ).find((element) => {
          return (
            element.dataset
              .deebStickyOwner ===
            this.stickyOwner
          );
        });

      this.stickyPurchase =
        this.root.querySelector(
          '[data-deeb-sticky-purchase]'
        ) ||
        mountedStickyPurchase ||
        this.stickyPurchase ||
        null;

      this.stickyButton =
        this.stickyPurchase
          ?.querySelector(
            '[data-deeb-sticky-submit]'
          ) || null;

      this.stickySpinner =
        this.stickyButton
          ?.querySelector(
            '.loading__spinner'
          ) || null;

      this.stickyFormat =
        this.stickyPurchase
          ?.querySelector(
            '[data-deeb-sticky-format]'
          ) || null;

      this.stickyPrice =
        this.stickyPurchase
          ?.querySelector(
            '[data-deeb-sticky-price]'
          ) || null;

      this.packageInfo =
        this.root.querySelector(
          '[data-deeb-package-info]'
        );

      this.packageLabel =
        this.root.querySelector(
          '[data-deeb-package-label]'
        );

      this.packageSummary =
        this.root.querySelector(
          '[data-deeb-package-summary]'
        );

      this.contractScript =
        this.root.querySelector(
          CONTRACT_SELECTOR
        );
    }

    normalizeContract(
      variantId,
      contract
    ) {
      const id = normalizeId(variantId);

      if (
        !id ||
        !contract ||
        typeof contract !== 'object'
      ) {
        return null;
      }

      const quantity =
        contract.quantity || {};

      const inventory =
        contract.inventory || {};

      const packageData =
        contract.package || {};

      return {
        id,
        state: normalizeState(
          contract.state
        ),
        available:
          contract.available === true,
        cartQuantity: Math.max(
          0,
          toFiniteNumber(
            contract.cartQuantity,
            0
          )
        ),
        title: String(
          contract.title || ''
        ).trim(),
        price: toFiniteNumber(
          contract.price,
          0
        ),
        priceText: String(
          contract.priceText || ''
        ).trim(),
        quantity: {
          min: Math.max(
            1,
            toFiniteNumber(
              quantity.min,
              1
            )
          ),
          increment: Math.max(
            1,
            toFiniteNumber(
              quantity.increment,
              1
            )
          ),
          max: toNullableNumber(
            quantity.max,
            null
          )
        },
        inventory: {
          management: String(
            inventory.management || ''
          ).trim(),
          policy: String(
            inventory.policy || ''
          ).trim(),
          quantity: toNullableNumber(
            inventory.quantity,
            null
          )
        },
        package: {
          label: String(
            packageData.label || ''
          ).trim(),
          count:
            packageData.count === null ||
            packageData.count === undefined
              ? ''
              : String(
                  packageData.count
                ).trim()
        }
      };
    }

    refreshVariantContracts() {
      const script =
        this.contractScript ||
        this.root.querySelector(
          CONTRACT_SELECTOR
        );

      if (!script) {
        this.variantContracts = new Map();
        this.contractScriptElement = null;
        this.contractScriptContent = '';
        return;
      }

      const scriptContent =
        script.textContent.trim();

      if (
        script ===
          this.contractScriptElement &&
        scriptContent ===
          this.contractScriptContent
      ) {
        return;
      }

      this.contractScriptElement = script;
      this.contractScriptContent =
        scriptContent;

      try {
        const parsed = JSON.parse(
          scriptContent || '{}'
        );

        const contracts = new Map();

        Object.entries(parsed).forEach(
          ([variantId, rawContract]) => {
            const contract =
              this.normalizeContract(
                variantId,
                rawContract
              );

            if (contract) {
              contracts.set(
                contract.id,
                contract
              );
            }
          }
        );

        this.variantContracts =
          contracts;
      } catch (error) {
        this.variantContracts =
          new Map();

        console.warn(
          'Deeb variant commerce contracts could not be read.',
          error
        );
      }
    }

    mountStickyPurchaseToBody() {
      if (!this.stickyPurchase) {
        return;
      }

      document
        .querySelectorAll(
          '[data-deeb-sticky-purchase]'
        )
        .forEach((element) => {
          if (
            element !==
              this.stickyPurchase &&
            element.dataset
              .deebStickyOwner ===
              this.stickyOwner
          ) {
            element.remove();
          }
        });

      if (!this.stickyOriginalParent) {
        this.stickyOriginalParent =
          this.stickyPurchase.parentNode;

        this.stickyOriginalNextSibling =
          this.stickyPurchase.nextSibling;
      }

      this.stickyPurchase.dataset
        .deebStickyOwner =
        this.stickyOwner;

      /*
       * The sticky bar normally inherits these design
       * tokens from the product section. Preserve them
       * before moving it to document.body.
       */
      const rootStyles =
        window.getComputedStyle(
          this.root
        );

      [
        '--deeb-ink',
        '--deeb-ink-soft',
        '--deeb-light',
        '--deeb-radius-pill'
      ].forEach((propertyName) => {
        const value =
          rootStyles
            .getPropertyValue(
              propertyName
            )
            .trim();

        if (value) {
          this.stickyPurchase.style
            .setProperty(
              propertyName,
              value
            );
        }
      });

      if (
        this.stickyPurchase.parentNode !==
        document.body
      ) {
        document.body.appendChild(
          this.stickyPurchase
        );
      }
    }

    restoreStickyPurchase() {
      if (!this.stickyPurchase) {
        return;
      }

      this.stickyPurchase
        .classList.remove(
          'is-visible'
        );

      this.stickyPurchase.hidden = true;

      this.stickyPurchase.setAttribute(
        'aria-hidden',
        'true'
      );

      if (
        this.stickyOriginalParent
          ?.isConnected
      ) {
        if (
          this.stickyOriginalNextSibling
            ?.parentNode ===
          this.stickyOriginalParent
        ) {
          this.stickyOriginalParent
            .insertBefore(
              this.stickyPurchase,
              this.stickyOriginalNextSibling
            );
        } else {
          this.stickyOriginalParent
            .appendChild(
              this.stickyPurchase
            );
        }
      } else {
        this.stickyPurchase.remove();
      }

      delete this.stickyPurchase.dataset
        .deebStickyOwner;

      [
        '--deeb-ink',
        '--deeb-ink-soft',
        '--deeb-light',
        '--deeb-radius-pill'
      ].forEach((propertyName) => {
        this.stickyPurchase.style
          .removeProperty(
            propertyName
          );
      });
    }

    initialize() {
      if (
        !this.purchaseBlock ||
        !this.primaryButton
      ) {
        return;
      }

      this.mountStickyPurchaseToBody();
      this.refreshElements();

      this.root.dataset
        .deebProductInitialized = 'true';

      this.root.addEventListener(
        'change',
        this.handleRootChange,
        true
      );

      /*
       * Native input values change as DOM properties,
       * not necessarily as observable HTML attributes.
       * Listen directly so typed quantities update the
       * purchase state before form submission.
       */
      this.root.addEventListener(
        'input',
        this.handleQuantityInput,
        true
      );

      window.addEventListener(
        'scroll',
        this.handleViewportChange,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        this.handleViewportChange,
        { passive: true }
      );

      if (
        typeof this.mobileMediaQuery
          .addEventListener === 'function'
      ) {
        this.mobileMediaQuery
          .addEventListener(
            'change',
            this.handleMediaQueryChange
          );
      } else {
        this.mobileMediaQuery.addListener(
          this.handleMediaQueryChange
        );
      }

      this.structureObserver =
        new MutationObserver(
          this.handleStructureMutation
        );

      this.purchaseObserver =
        new MutationObserver(
          this.handlePurchaseMutation
        );

      this.quantityObserver =
        new MutationObserver(
          this.handleQuantityMutation
        );

      this.connectObservers();

      this.syncVariant(
        this.getCurrentVariantId()
      );

      this.preserveQueryParameters();
      this.updateStickyVisibility();
    }

    disconnectObservers() {
      this.structureObserver?.disconnect();
      this.purchaseObserver?.disconnect();
      this.quantityObserver?.disconnect();
    }

    connectObservers() {
      if (
        this.destroyed ||
        !this.root.isConnected
      ) {
        return;
      }

      this.disconnectObservers();

      /*
       * Observe only structural replacements inside the
       * information column. Dawn can replace the purchase
       * controls after a variant request.
       */
      if (this.infoContainer) {
        this.structureObserver.observe(
          this.infoContainer,
          {
            subtree: true,
            childList: true
          }
        );
      }

      /*
       * Product form loading only needs the primary button.
       * This mirrors loading and disabled states to Sticky.
       */
      if (this.primaryButton) {
        this.purchaseObserver.observe(
          this.primaryButton,
          {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: [
              'class',
              'disabled',
              'aria-disabled'
            ]
          }
        );
      }

      /*
       * Quantity rules and cart quantity are owned by Dawn.
       * Observe their attributes instead of the entire page.
       */
      if (this.quantityInput) {
        this.quantityObserver.observe(
          this.quantityInput,
          {
            attributes: true,
            attributeFilter: [
              'data-cart-quantity',
              'data-min',
              'data-max',
              'min',
              'max',
              'step',
              'value'
            ]
          }
        );
      }
    }

    withObserversPaused(callback) {
      this.disconnectObservers();

      try {
        callback();
      } finally {
        this.structureObserver
          ?.takeRecords();

        this.purchaseObserver
          ?.takeRecords();

        this.quantityObserver
          ?.takeRecords();

        this.connectObservers();
      }
    }

    handleStructureMutation() {
      this.refreshElements();
      this.refreshVariantContracts();
      this.connectObservers();

      if (
        this.variantTransitionActive
      ) {
        this.applyTransitionPresentation();
        return;
      }

      this.scheduleSync();
    }

    handlePurchaseMutation() {
      this.refreshElements();
      this.connectObservers();

      if (
        this.variantTransitionActive
      ) {
        this.applyTransitionPresentation();
        return;
      }

      this.scheduleSync();
    }

    handleQuantityMutation() {
      if (
        this.variantTransitionActive
      ) {
        return;
      }

      this.scheduleSync();
    }

    getButtonLabel(button, state) {
      if (!button) {
        return '';
      }

      if (state === 'available') {
        return (
          button.dataset.deebAddLabel ||
          this.primaryButton?.dataset
            .deebAddLabel ||
          'Add to Humidor'
        );
      }

      if (state === 'unavailable') {
        return (
          button.dataset
            .deebUnavailableLabel ||
          this.primaryButton?.dataset
            .deebUnavailableLabel ||
          'Unavailable'
        );
      }

      if (state === 'invalid-quantity') {
        return (
          button.dataset
            .deebInvalidQuantityLabel ||
          this.primaryButton?.dataset
            .deebInvalidQuantityLabel ||
          'Check quantity'
        );
      }

      if (state === 'maximum-reached') {
        return (
          button.dataset
            .deebMaximumReachedLabel ||
          button.dataset
            .deebLimitReachedLabel ||
          this.primaryButton?.dataset
            .deebMaximumReachedLabel ||
          this.primaryButton?.dataset
            .deebLimitReachedLabel ||
          'Maximum reached'
        );
      }

      if (state === 'insufficient-stock') {
        return (
          button.dataset
            .deebInsufficientStockLabel ||
          this.primaryButton?.dataset
            .deebInsufficientStockLabel ||
          'Not enough stock'
        );
      }

      return (
        button.dataset.deebSoldOutLabel ||
        this.primaryButton?.dataset
          .deebSoldOutLabel ||
        'Sold out'
      );
    }

    getCurrentButtonText() {
      const label =
        this.primaryButton?.querySelector(
          '[data-deeb-submit-text]'
        );

      return String(
        label?.textContent || ''
      ).trim();
    }

    beginVariantTransition() {
      const currentState =
        normalizeState(
          this.primaryButton?.dataset
            .deebSubmitState ||
          this.purchaseBlock?.dataset
            .deebSubmitState
        );

      this.transitionLabel =
        this.getCurrentButtonText() ||
        this.getButtonLabel(
          this.primaryButton,
          currentState
        ) ||
        'Add to Humidor';

      this.variantTransitionActive = true;

      window.clearTimeout(
        this.transitionTimer
      );

      this.applyTransitionPresentation();

      this.transitionTimer =
        window.setTimeout(() => {
          this.variantTransitionActive =
            false;

          this.transitionLabel = '';
          this.transitionTimer = null;

          this.refreshElements();
          this.refreshVariantContracts();

          this.syncVariant(
            this.getCurrentVariantId()
          );
        }, TRANSITION_TIMEOUT);
    }

    applyTransitionPresentation() {
      if (
        !this.variantTransitionActive
      ) {
        return;
      }

      this.withObserversPaused(() => {
        this.refreshElements();

        [
          this.primaryButton,
          this.stickyButton
        ].forEach((button) => {
          if (!button) {
            return;
          }

          const label =
            button.querySelector(
              '[data-deeb-submit-text]'
            );

          if (
            label &&
            this.transitionLabel &&
            label.textContent.trim() !==
              this.transitionLabel
          ) {
            label.textContent =
              this.transitionLabel;
          }

          button.disabled = true;

          button.setAttribute(
            'aria-disabled',
            'true'
          );
        });
      });
    }

    settleVariantTransition() {
      window.clearTimeout(
        this.transitionTimer
      );

      this.transitionTimer = null;
      this.variantTransitionActive = false;
      this.transitionLabel = '';
    }

    handleRootChange(event) {
      const target = event.target;

      if (
        !(
          target instanceof
            HTMLInputElement
        ) &&
        !(
          target instanceof
            HTMLSelectElement
        )
      ) {
        return;
      }

      if (
        target.matches(
          '.quantity__input[name="quantity"]'
        )
      ) {
        this.scheduleSync();
        return;
      }

      if (
        !target.closest(
          VARIANT_PICKER_SELECTOR
        )
      ) {
        return;
      }

      this.refreshElements();
      this.beginVariantTransition();
    }

    handleQuantityInput(event) {
      const target = event.target;

      if (
        !(
          target instanceof
            HTMLInputElement
        ) ||
        !target.matches(
          '.quantity__input[name="quantity"]'
        )
      ) {
        return;
      }

      this.scheduleSync();
    }

    handleViewportChange() {
      if (
        this.viewportFrameRequested
      ) {
        return;
      }

      this.viewportFrameRequested = true;

      window.requestAnimationFrame(() => {
        this.viewportFrameRequested =
          false;

        if (
          this.destroyed ||
          !this.root.isConnected
        ) {
          return;
        }

        this.updateStickyVisibility();
      });
    }

    handleMediaQueryChange() {
      this.updateStickyVisibility();
    }

    scheduleSync(
      variantId,
      settleTransition = false
    ) {
      if (this.destroyed) {
        return;
      }

      if (variantId !== undefined) {
        this.pendingVariantId =
          normalizeId(variantId);
      }

      if (settleTransition) {
        this.pendingSettleTransition =
          true;
      }

      if (this.syncScheduled) {
        return;
      }

      this.syncScheduled = true;

      afterRender(() => {
        this.syncScheduled = false;

        if (
          this.destroyed ||
          !this.root.isConnected
        ) {
          this.pendingVariantId =
            undefined;

          this.pendingSettleTransition =
            false;

          return;
        }

        const pendingVariantId =
          this.pendingVariantId;

        const shouldSettleTransition =
          this.pendingSettleTransition;

        this.pendingVariantId =
          undefined;

        this.pendingSettleTransition =
          false;

        this.refreshElements();
        this.refreshVariantContracts();

        if (
          shouldSettleTransition
        ) {
          this.settleVariantTransition();
        }

        this.syncVariant(
          pendingVariantId !== undefined
            ? pendingVariantId
            : this.getCurrentVariantId()
        );

        this.preserveQueryParameters();
        this.updateStickyVisibility();
      });
    }

    handleVariantChange(
      variant,
      hasVariant
    ) {
      const variantId =
        hasVariant &&
        variant?.id !== undefined
          ? normalizeId(variant.id)
          : hasVariant
            ? ''
            : undefined;

      this.scheduleSync(
        variantId,
        true
      );
    }

    getCurrentVariantId() {
      return normalizeId(
        this.variantInput?.value
      );
    }

    getFallbackState() {
      return normalizeState(
        this.primaryButton?.dataset
          .deebSubmitState ||
        this.purchaseBlock?.dataset
          .deebSubmitState ||
        'unavailable'
      );
    }

    getQuantitySnapshot(contract) {
      const cartQuantity = Math.max(
        0,
        toFiniteNumber(
          this.quantityInput?.dataset
            .cartQuantity,
          contract?.cartQuantity || 0
        )
      );

      const minimum = Math.max(
        1,
        toFiniteNumber(
          this.quantityInput?.dataset
            .min ||
            this.quantityInput?.min,
          contract?.quantity.min || 1
        )
      );

      const increment = Math.max(
        1,
        toFiniteNumber(
          this.quantityInput?.step,
          contract?.quantity.increment || 1
        )
      );

      const maximum =
        toNullableNumber(
          this.quantityInput?.dataset
            .max ||
            this.quantityInput?.max,
          contract?.quantity.max ?? null
        );

      const requestedQuantity =
        toNullableNumber(
          this.quantityInput?.value,
          null
        );

      /*
       * HTML number inputs use min as the step base.
       * A valid requested quantity therefore follows:
       *
       * minimum + (n * increment)
       *
       * The tolerance avoids floating-point noise while
       * still respecting Shopify's exposed quantity rule.
       */
      const incrementOffset =
        requestedQuantity === null
          ? null
          : (
              requestedQuantity -
              minimum
            ) / increment;

      const followsIncrement =
        incrementOffset !== null &&
        Math.abs(
          incrementOffset -
          Math.round(incrementOffset)
        ) < 1e-9;

      const invalidQuantity =
        requestedQuantity === null ||
        requestedQuantity <= 0 ||
        requestedQuantity < minimum ||
        !followsIncrement ||
        Boolean(
          this.quantityInput?.validity
            ?.badInput
        );

      return {
        cartQuantity,
        minimum,
        increment,
        maximum,
        requestedQuantity,
        invalidQuantity,
        nextCartQuantity:
          cartQuantity +
          (requestedQuantity ?? 0)
      };
    }

    resolvePurchaseState(contract) {
      if (!contract) {
        return this.getFallbackState();
      }

      if (
        contract.state !== 'available'
      ) {
        return contract.state;
      }

      const quantity =
        this.getQuantitySnapshot(
          contract
        );

      if (quantity.invalidQuantity) {
        return 'invalid-quantity';
      }

      if (
        quantity.maximum !== null &&
        quantity.nextCartQuantity >
          quantity.maximum
      ) {
        return 'maximum-reached';
      }

      const inventoryIsLimited =
        contract.inventory.management ===
          'shopify' &&
        contract.inventory.policy !==
          'continue';

      if (
        inventoryIsLimited &&
        contract.inventory.quantity !==
          null &&
        quantity.nextCartQuantity >
          contract.inventory.quantity
      ) {
        return 'insufficient-stock';
      }

      return 'available';
    }

    applyButtonState(button, state) {
      if (!button) {
        return;
      }

      const submissionActive =
        button === this.primaryButton &&
        button.classList.contains(
          'loading'
        );

      button.dataset.deebSubmitState =
        state;

      if (submissionActive) {
        button.disabled = true;

        button.setAttribute(
          'aria-disabled',
          'true'
        );

        return;
      }

      const label =
        button.querySelector(
          '[data-deeb-submit-text]'
        );

      const nextText =
        this.getButtonLabel(
          button,
          state
        );

      if (
        label &&
        label.textContent.trim() !==
          nextText
      ) {
        label.textContent = nextText;
      }

      const shouldDisable =
        state !== 'available';

      button.disabled = shouldDisable;

      if (shouldDisable) {
        button.setAttribute(
          'aria-disabled',
          'true'
        );
      } else {
        button.removeAttribute(
          'aria-disabled'
        );
      }
    }

    syncSubmittingState() {
      if (!this.stickyButton) {
        return;
      }

      const isSubmitting = Boolean(
        this.primaryButton?.classList
          .contains('loading')
      );

      this.stickyButton.classList.toggle(
        'loading',
        isSubmitting
      );

      this.stickyButton.toggleAttribute(
        'aria-busy',
        isSubmitting
      );

      if (this.stickySpinner) {
        this.stickySpinner
          .classList.toggle(
            'hidden',
            !isSubmitting
          );
      }

      if (isSubmitting) {
        this.stickyButton.disabled =
          true;

        this.stickyButton.setAttribute(
          'aria-disabled',
          'true'
        );

        return;
      }

      const state = normalizeState(
        this.stickyButton.dataset
          .deebSubmitState
      );

      const shouldDisable =
        state !== 'available';

      this.stickyButton.disabled =
        shouldDisable;

      if (shouldDisable) {
        this.stickyButton.setAttribute(
          'aria-disabled',
          'true'
        );
      } else {
        this.stickyButton.removeAttribute(
          'aria-disabled'
        );
      }
    }

    updateVariantInput(
      variantId,
      state
    ) {
      if (!this.variantInput) {
        return;
      }

      if (variantId) {
        this.variantInput.value =
          variantId;
      }

      this.variantInput.disabled =
        state !== 'available';
    }

    getPackageSummary(packageData) {
      if (
        !packageData?.label ||
        !packageData?.count
      ) {
        return '';
      }

      if (
        packageData.label
          .toLowerCase()
          .includes('box')
      ) {
        return `Box of ${packageData.count}`;
      }

      return (
        `${packageData.label} of ` +
        `${packageData.count}`
      );
    }

    syncPackageInformation(
      variantId,
      contract
    ) {
      if (!this.packageInfo) {
        return;
      }

      const packageData =
        contract?.package || null;

      const summary =
        this.getPackageSummary(
          packageData
        );

      const shouldShow =
        Boolean(summary);

      this.packageInfo.dataset.variantId =
        variantId;

      if (!shouldShow) {
        this.packageInfo.hidden = true;

        this.packageInfo.removeAttribute(
          'aria-label'
        );

        if (this.packageLabel) {
          this.packageLabel.textContent =
            '';
        }

        if (this.packageSummary) {
          this.packageSummary.textContent =
            '';
        }

        return;
      }

      if (this.packageLabel) {
        this.packageLabel.textContent =
          packageData.label;
      }

      if (this.packageSummary) {
        this.packageSummary.textContent =
          summary;
      }

      this.packageInfo.hidden = false;

      this.packageInfo.setAttribute(
        'aria-label',
        `${packageData.label}: ${summary}`
      );
    }

    getSelectedFormat(contract) {
      if (
        contract?.title &&
        contract.title !==
          'Default Title'
      ) {
        return contract.title;
      }

      const selectedValues = [];

      this.root
        .querySelectorAll(
          [
            'variant-selects input[type="radio"]:checked',
            'variant-radios input[type="radio"]:checked',
            '.product-form__input input[type="radio"]:checked',
            'variant-selects select',
            'variant-radios select'
          ].join(',')
        )
        .forEach((field) => {
          const value = String(
            field.value || ''
          ).trim();

          if (
            value &&
            value !== 'Default Title' &&
            !selectedValues.includes(
              value
            )
          ) {
            selectedValues.push(value);
          }
        });

      return selectedValues.join(' / ');
    }

    syncStickyContent(contract) {
      const format =
        this.getSelectedFormat(
          contract
        );

      const price =
        contract?.priceText || '';

      if (
        this.stickyFormat &&
        format
      ) {
        this.stickyFormat.textContent =
          format;
      }

      if (
        this.stickyPrice &&
        price
      ) {
        this.stickyPrice.textContent =
          price;
      }
    }

    syncVariant(requestedVariantId) {
      this.refreshElements();
      this.refreshVariantContracts();

      const variantId = normalizeId(
        requestedVariantId ||
        this.getCurrentVariantId()
      );

      const contract = variantId
        ? this.variantContracts.get(
            variantId
          ) || null
        : null;

      const state = variantId
        ? this.resolvePurchaseState(
            contract
          )
        : 'unavailable';

      this.withObserversPaused(() => {
        this.refreshElements();

        this.updateVariantInput(
          variantId,
          state
        );

        this.applyButtonState(
          this.primaryButton,
          state
        );

        this.applyButtonState(
          this.stickyButton,
          state
        );

        if (this.purchaseBlock) {
          this.purchaseBlock.dataset
            .deebSubmitState = state;
        }

        if (this.stickyPurchase) {
          this.stickyPurchase.dataset
            .deebSubmitState = state;
        }

        this.syncPackageInformation(
          variantId,
          contract
        );

        this.syncStickyContent(
          contract
        );

        this.syncSubmittingState();
      });
    }

    preserveQueryParameters() {
      if (
        this.preservedQueryParameters
          .size === 0
      ) {
        return;
      }

      const currentUrl = new URL(
        window.location.href
      );

      let changed = false;

      this.preservedQueryParameters
        .forEach((value, key) => {
          if (
            currentUrl.searchParams
              .get(key) !== value
          ) {
            currentUrl.searchParams.set(
              key,
              value
            );

            changed = true;
          }
        });

      if (!changed) {
        return;
      }

      window.history.replaceState(
        window.history.state,
        '',
        `${currentUrl.pathname}` +
          `${currentUrl.search}` +
          `${currentUrl.hash}`
      );
    }

    updateStickyVisibility() {
      if (
        !this.stickyPurchase ||
        !this.primaryButton
      ) {
        return;
      }

      if (
        !this.mobileMediaQuery.matches
      ) {
        this.stickyPurchase
          .classList.remove(
            'is-visible'
          );

        this.stickyPurchase.hidden = true;

        this.stickyPurchase.setAttribute(
          'aria-hidden',
          'true'
        );

        return;
      }

      this.stickyPurchase.hidden = false;

      const primaryButtonRect =
        this.primaryButton
          .getBoundingClientRect();

      const stickyIsVisible =
        this.stickyPurchase
          .classList.contains(
            'is-visible'
          );

      /*
       * Use separate show and hide thresholds.
       * Mobile browser chrome and late layout updates can
       * move the CTA a few pixels around the viewport edge.
       * Without hysteresis that jitter makes the sticky bar
       * appear and disappear immediately.
       */
      const shouldShow =
        stickyIsVisible
          ? primaryButtonRect.bottom <
              STICKY_HIDE_THRESHOLD
          : primaryButtonRect.bottom <=
              STICKY_SHOW_THRESHOLD;

      this.stickyPurchase
        .classList.toggle(
          'is-visible',
          shouldShow
        );

      this.stickyPurchase.setAttribute(
        'aria-hidden',
        shouldShow
          ? 'false'
          : 'true'
      );
    }

    destroy() {
      if (this.destroyed) {
        return;
      }

      this.destroyed = true;
      this.syncScheduled = false;
      this.pendingVariantId = undefined;
      this.pendingSettleTransition = false;
      this.viewportFrameRequested = false;

      this.settleVariantTransition();
      this.disconnectObservers();

      this.root.removeEventListener(
        'change',
        this.handleRootChange,
        true
      );

      this.root.removeEventListener(
        'input',
        this.handleQuantityInput,
        true
      );

      window.removeEventListener(
        'scroll',
        this.handleViewportChange
      );

      window.removeEventListener(
        'resize',
        this.handleViewportChange
      );

      if (
        typeof this.mobileMediaQuery
          .removeEventListener ===
          'function'
      ) {
        this.mobileMediaQuery
          .removeEventListener(
            'change',
            this.handleMediaQueryChange
          );
      } else {
        this.mobileMediaQuery
          .removeListener(
            this.handleMediaQueryChange
          );
      }

      this.restoreStickyPurchase();

      delete this.root.dataset
        .deebProductInitialized;
    }
  }

  const releaseController = (controller) => {
    if (!controller) {
      return;
    }

    controller.destroy();
    activeControllers.delete(controller);
    controllers.delete(controller.root);
  };

  const controllerBelongsToScope = (
    controller,
    scope
  ) => {
    if (!controller || !scope) {
      return false;
    }

    if (scope === document) {
      return true;
    }

    const root = controller.root;

    if (scope === root) {
      return true;
    }

    return (
      typeof scope.contains ===
        'function' &&
      scope.contains(root)
    );
  };

  const runtime = {
    version: RUNTIME_VERSION,
    unsubscribeVariantChange: null,
    handleSectionLoad: null,
    handleSectionUnload: null,

    initAll(scope = document) {
      const roots = [];

      if (
        scope instanceof Element &&
        scope.matches(ROOT_SELECTOR)
      ) {
        roots.push(scope);
      }

      if (
        scope &&
        typeof scope.querySelectorAll ===
          'function'
      ) {
        scope
          .querySelectorAll(
            ROOT_SELECTOR
          )
          .forEach((root) => {
            roots.push(root);
          });
      }

      roots.forEach((root) => {
        const existingController =
          controllers.get(root);

        if (
          existingController &&
          !existingController.destroyed
        ) {
          activeControllers.add(
            existingController
          );
          return;
        }

        if (existingController) {
          releaseController(
            existingController
          );
        }

        const controller =
          new DeebCigarProductController(
            root
          );

        controllers.set(root, controller);
        activeControllers.add(controller);
      });
    },

    destroyAll(scope = document) {
      Array.from(activeControllers)
        .filter((controller) => {
          return controllerBelongsToScope(
            controller,
            scope
          );
        })
        .forEach(releaseController);
    },

    handleVariantEvent(eventPayload) {
      const eventData =
        eventPayload?.data ||
        eventPayload ||
        {};

      const hasVariant =
        Object.prototype
          .hasOwnProperty.call(
            eventData,
            'variant'
          );

      const variant = hasVariant
        ? eventData.variant
        : undefined;

      const sectionId =
        eventData.sectionId ||
        eventData.section_id ||
        eventData.section?.id ||
        '';

      Array.from(activeControllers)
        .forEach((controller) => {
          if (
            controller.destroyed ||
            !controller.root.isConnected
          ) {
            releaseController(controller);
            return;
          }

          if (
            sectionId &&
            String(
              controller.root.dataset.section
            ) !== String(sectionId)
          ) {
            return;
          }

          controller.handleVariantChange(
            variant,
            hasVariant
          );
        });
    },

    destroyRuntime() {
      Array.from(activeControllers)
        .forEach(releaseController);

      if (
        typeof this
          .unsubscribeVariantChange ===
          'function'
      ) {
        this.unsubscribeVariantChange();
      }

      this.unsubscribeVariantChange =
        null;

      if (this.handleSectionLoad) {
        document.removeEventListener(
          'shopify:section:load',
          this.handleSectionLoad
        );
      }

      if (this.handleSectionUnload) {
        document.removeEventListener(
          'shopify:section:unload',
          this.handleSectionUnload
        );
      }

      this.handleSectionLoad = null;
      this.handleSectionUnload = null;

      if (window[GLOBAL_KEY] === this) {
        delete window[GLOBAL_KEY];
      }
    }
  };

  window[GLOBAL_KEY] = runtime;

  runtime.initAll(document);

  const subscribeFunction =
    typeof window.subscribe ===
      'function'
      ? window.subscribe
      : typeof subscribe ===
          'function'
        ? subscribe
        : null;

  const pubSubEvents =
    window.PUB_SUB_EVENTS ||
    (
      typeof PUB_SUB_EVENTS !==
        'undefined'
        ? PUB_SUB_EVENTS
        : null
    );

  if (
    subscribeFunction &&
    pubSubEvents?.variantChange
  ) {
    runtime.unsubscribeVariantChange =
      subscribeFunction(
        pubSubEvents.variantChange,
        (eventPayload) => {
          runtime.handleVariantEvent(
            eventPayload
          );
        }
      );
  }

  runtime.handleSectionLoad =
    (event) => {
      runtime.initAll(event.target);
    };

  runtime.handleSectionUnload =
    (event) => {
      runtime.destroyAll(
        event.target
      );
    };

  document.addEventListener(
    'shopify:section:load',
    runtime.handleSectionLoad
  );

  document.addEventListener(
    'shopify:section:unload',
    runtime.handleSectionUnload
  );
})();
